import type { Capability, Membership } from '@mfos/shared';
import { ADMIN_CAPABILITIES, canAny } from '../workspace/membership-context';

export type AdminTabGroup = 'people' | 'ownership' | 'oversight' | 'settings';

export const ADMIN_TAB_GROUPS: readonly AdminTabGroup[] = [
  'people',
  'ownership',
  'oversight',
  'settings',
];

export interface AdminTab {
  readonly path: string;
  readonly label: string;
  readonly group: AdminTabGroup;
  /** Any of these capabilities shows the tab (deny by default). */
  readonly capabilities: readonly Capability[];
}

const GOVERNANCE_CAPABILITIES: readonly Capability[] = ['governance.read', 'governance.manage'];

export const ADMIN_TABS: readonly AdminTab[] = [
  {
    path: 'members',
    label: 'Members',
    group: 'people',
    capabilities: ['members.manage', 'members.read'],
  },
  { path: 'invitations', label: 'Invitations', group: 'people', capabilities: ['members.invite'] },
  {
    path: 'permissions',
    label: 'Permission grants',
    group: 'people',
    capabilities: ['permissions.manage'],
  },
  {
    path: 'founders',
    label: 'Founders',
    group: 'people',
    capabilities: ['founders.manage', 'obligations.manage'],
  },
  { path: 'equity', label: 'Equity', group: 'ownership', capabilities: ['equity.manage'] },
  {
    path: 'governance',
    label: 'Governance',
    group: 'ownership',
    capabilities: GOVERNANCE_CAPABILITIES,
  },
  { path: 'finance', label: 'Finance', group: 'ownership', capabilities: ['finance.manage'] },
  { path: 'audit', label: 'Audit log', group: 'oversight', capabilities: ['audit.read'] },
  { path: 'ai', label: 'AI activity', group: 'oversight', capabilities: ['audit.read'] },
  {
    path: 'execution',
    label: 'Execution & reporting',
    group: 'settings',
    capabilities: ['settings.manage'],
  },
  {
    path: 'notifications',
    label: 'Notifications',
    group: 'settings',
    capabilities: ['settings.manage'],
  },
  {
    path: 'security',
    label: 'Security settings',
    group: 'settings',
    capabilities: ['settings.manage'],
  },
  { path: 'system', label: 'System status', group: 'settings', capabilities: ADMIN_CAPABILITIES },
];

export function hasAdminAccess(membership: Membership): boolean {
  return canAny(membership, ADMIN_CAPABILITIES) || canAny(membership, GOVERNANCE_CAPABILITIES);
}

export function visibleAdminTabs(membership: Membership): AdminTab[] {
  if (!hasAdminAccess(membership)) {
    return [];
  }
  return ADMIN_TABS.filter((tab) => canAny(membership, tab.capabilities));
}

export function adminTab(path: string): AdminTab {
  const tab = ADMIN_TABS.find((item) => item.path === path);
  if (tab === undefined) {
    throw new Error(`Unknown admin tab: ${path}`);
  }
  return tab;
}
